import React, { useState, useEffect, useCallback } from 'react';
import { pmsAPI } from '../../../services';
import { Target, Users, CheckCircle, Clock, AlertCircle, Star, TrendingUp, X, RefreshCw, ChevronRight, Info } from 'lucide-react';

const YEAR = new Date().getFullYear();

const OUTCOMES = [
  { v:'increment',  l:'Salary Increment', color:'bg-green-100 text-green-700',  minKpi:3.5 },
  { v:'promotion',  l:'Promotion',        color:'bg-blue-100 text-blue-700',    minKpi:4.5 },
  { v:'recognition',l:'Recognition',      color:'bg-yellow-100 text-yellow-700',minKpi:2.5 },
  { v:'pip',        l:'PIP',              color:'bg-red-100 text-red-700',      maxKpi:2.0 },
  { v:'no_change',  l:'No Change',        color:'bg-gray-100 text-gray-600',    minKpi:0   }
];

const suggestOutcome = (kpi) => {
  if (!kpi) return 'no_change';
  const k = parseFloat(kpi);
  if (k >= 4.5) return 'promotion';
  if (k >= 3.5) return 'increment';
  if (k >= 2.5) return 'recognition';
  if (k < 2.0) return 'pip';
  return 'no_change';
};

const GOAL_STATUS_COLORS = {
  draft:'bg-gray-100 text-gray-600', discussion:'bg-blue-100 text-blue-700',
  pending_approval:'bg-yellow-100 text-yellow-800', approved:'bg-green-100 text-green-700',
  resubmit_requested:'bg-red-100 text-red-700'
};

const PmsPage = () => {
  const [data, setData] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const [teamGoals, setTeamGoals] = useState([]);
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [showOutcome, setShowOutcome] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({ outcome:'no_change', notes:'' });
  const [showRating, setShowRating] = useState(null);
  const [ratingForm, setRatingForm] = useState({ managerRating:'', managerComment:'' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [selectedEmp, setSelectedEmp] = useState(null); // for side panel

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [dash, out, tg] = await Promise.all([
        pmsAPI.getAdminDashboard(YEAR),
        pmsAPI.getOutcomes(YEAR),
        pmsAPI.getTeamGoals(YEAR)
      ]);
      setData(dash?.data || null);
      setOutcomes(Array.isArray(out?.data) ? out.data : []);
      setTeamGoals(Array.isArray(tg?.data) ? tg.data : []);
    } catch { showToast('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReviewGoal = async (id, action, managerNote) => {
    try { await pmsAPI.reviewGoal(id, { action, managerNote }); showToast('Goal reviewed'); load(); }
    catch { showToast('Failed', 'error'); }
  };

  const handleManagerRating = async () => {
    if (!ratingForm.managerRating) return showToast('Please select a rating', 'error');
    try {
      setSaving(true);
      await pmsAPI.submitManagerRating(showRating._id, { managerRating: parseInt(ratingForm.managerRating), managerComment: ratingForm.managerComment });
      showToast('Rating submitted'); setShowRating(null); load();
    } catch { showToast('Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleSetOutcome = async () => {
    try {
      setSaving(true);
      const empRow = data?.employees?.find(e => e.employee?._id === showOutcome?.employee?._id);
      await pmsAPI.setOutcome({ employeeId: showOutcome.employee._id, year: YEAR, outcome: outcomeForm.outcome, notes: outcomeForm.notes, kpiScore: parseFloat(empRow?.kpiScore) || null });
      showToast('Outcome saved'); setShowOutcome(null); load();
    } catch { showToast('Failed', 'error'); }
    finally { setSaving(false); }
  };

  const openOutcomeModal = (empRow, existingOutcome) => {
    const suggested = suggestOutcome(empRow.kpiScore);
    setShowOutcome(empRow);
    setOutcomeForm({ outcome: existingOutcome?.outcome || suggested, notes: existingOutcome?.notes || '' });
  };

  const pendingGoals = teamGoals.filter(g => g.status === 'pending_approval');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Target className="w-6 h-6 text-blue-600"/>Performance Management — {YEAR}</h1>
          <p className="text-sm text-gray-500 mt-1">Review goals, rate performance, set annual outcomes</p>
        </div>
        <button onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4"/></button>
      </div>

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label:'Total Goals',    value:data.totalGoals, icon:Target,      color:'text-blue-600',  bg:'bg-blue-50'  },
            { label:'Approved',       value:data.approved,   icon:CheckCircle, color:'text-green-600', bg:'bg-green-50' },
            { label:'Pending Review', value:data.pending,    icon:Clock,       color:'text-yellow-600',bg:'bg-yellow-50'},
            { label:'Resubmit',       value:data.resubmit,   icon:AlertCircle, color:'text-red-600',   bg:'bg-red-50'   }
          ].map(({ label, value, icon:Icon, color, bg }) => (
            <div key={label} className={`${bg} rounded-xl p-4 flex items-center gap-3`}>
              <Icon className={`w-8 h-8 ${color}`}/><div><p className="text-xs text-gray-500">{label}</p><p className={`text-xl font-bold ${color}`}>{value}</p></div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
        {[['dashboard','KPI Dashboard'],['review','Goal Review'],['outcomes','Outcomes']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab===k?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>
            {l}{k==='review' && pendingGoals.length>0 && <span className="ml-1 bg-red-500 text-white rounded-full px-1.5 py-0.5 text-xs">{pendingGoals.length}</span>}
          </button>
        ))}
      </div>

      {/* KPI Dashboard with optional side panel */}
      {tab === 'dashboard' && (
        <div className={`flex gap-4 ${selectedEmp ? 'items-start' : ''}`}>
          {/* Main table */}
          <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden flex-1 min-w-0`}>
            <div className="p-4 border-b border-gray-100 font-semibold text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500"/>Employee KPI Overview
              {selectedEmp && <button onClick={() => setSelectedEmp(null)} className="ml-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"><X className="w-3 h-3"/>Close Panel</button>}
            </div>
            {loading ? <div className="py-10 text-center text-gray-400">Loading...</div>
            : data?.employees?.length === 0 ? <div className="py-10 text-center text-gray-400">No data</div>
            : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['Employee','Dept','Goals','Self Rated','KPI Score','Outcome','Action'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.employees?.map((row) => {
                    const { employee, goalCount, kpiScore, allGoalsSelfRated, approvedCount } = row;
                    const outcome = outcomes.find(o => o.employee?._id === employee?._id);
                    const oc = OUTCOMES.find(o => o.v === outcome?.outcome);
                    const isSelected = selectedEmp?.employee?._id === employee?._id;
                    const canSetOutcome = allGoalsSelfRated && kpiScore;
                    return (
                      <tr
                        key={employee?._id}
                        onClick={() => setSelectedEmp(isSelected ? null : row)}
                        className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-900">{employee?.firstName} {employee?.lastName}</span>
                            <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform ${isSelected ? 'rotate-90' : ''}`}/>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{employee?.department || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{goalCount}</td>
                        <td className="px-4 py-3">
                          {approvedCount === 0
                            ? <span className="text-gray-400 text-xs">No approved goals</span>
                            : allGoalsSelfRated
                              ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">✓ All Rated</span>
                              : <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 w-fit"><Info className="w-3 h-3"/>Pending</span>
                          }
                        </td>
                        <td className="px-4 py-3">
                          {kpiScore
                            ? <span className="font-bold text-blue-700">{kpiScore}/5 <Star className="w-3 h-3 inline text-yellow-500"/></span>
                            : <span className="text-gray-400 text-xs">Awaiting ratings</span>
                          }
                        </td>
                        <td className="px-4 py-3">
                          {oc ? <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${oc.color}`}>{oc.l}</span> : <span className="text-gray-400 text-xs">Not set</span>}
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          {canSetOutcome
                            ? <button onClick={() => openOutcomeModal(row, outcome)} className="text-xs text-blue-600 hover:underline font-medium">Set Outcome</button>
                            : <span className="text-xs text-gray-400" title={!allGoalsSelfRated ? 'Employee must self-rate all goals first' : 'Manager ratings pending'}>Locked</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Employee Goal Detail Side Panel */}
          {selectedEmp && (
            <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-gray-200 overflow-hidden self-start sticky top-4">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{selectedEmp.employee?.firstName} {selectedEmp.employee?.lastName}</p>
                  <p className="text-xs text-gray-400">{selectedEmp.employee?.department}</p>
                </div>
                <button onClick={() => setSelectedEmp(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4"/></button>
              </div>
              <div className="p-4 border-b border-gray-100 grid grid-cols-2 gap-2">
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-blue-700">{selectedEmp.kpiScore ? `${selectedEmp.kpiScore}/5` : '—'}</p>
                  <p className="text-xs text-gray-500">KPI Score</p>
                </div>
                <div className={`rounded-lg p-2 text-center ${selectedEmp.allGoalsSelfRated ? 'bg-green-50' : 'bg-yellow-50'}`}>
                  <p className={`text-xs font-semibold ${selectedEmp.allGoalsSelfRated ? 'text-green-700' : 'text-yellow-700'}`}>
                    {selectedEmp.allGoalsSelfRated ? '✓ All Self Rated' : 'Pending Self Rating'}
                  </p>
                  <p className="text-xs text-gray-500">{selectedEmp.approvedCount} approved</p>
                </div>
              </div>
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {(!selectedEmp.goals || selectedEmp.goals.length === 0)
                  ? <p className="text-sm text-gray-400 text-center py-4">No goals</p>
                  : selectedEmp.goals.map(g => (
                    <div key={g._id} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-sm font-semibold text-gray-900 flex-1 min-w-0 truncate">{g.title}</p>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${GOAL_STATUS_COLORS[g.status]}`}>{g.status.replace(/_/g,' ')}</span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">{g.weightage}% weight</p>
                      <div className="flex gap-3 text-xs">
                        <div>
                          <span className="text-gray-400">Self: </span>
                          <span className={`font-semibold ${g.selfRating ? 'text-purple-700' : 'text-gray-400'}`}>{g.selfRating ? `${g.selfRating}/5` : 'Not rated'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Manager: </span>
                          <span className={`font-semibold ${g.managerRating ? 'text-blue-700' : 'text-gray-400'}`}>{g.managerRating ? `${g.managerRating}/5` : '—'}</span>
                        </div>
                      </div>
                      {g.selfComment && <p className="text-xs text-gray-400 mt-1 italic truncate">"{g.selfComment}"</p>}
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </div>
      )}

      {/* Goal Review */}
      {tab === 'review' && (
        <div className="space-y-3">
          {teamGoals.length === 0 ? <div className="py-12 text-center text-gray-400 bg-white rounded-xl border border-gray-200">No goals to review</div>
          : teamGoals.map(g => (
            <div key={g._id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-semibold text-gray-900">{g.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${GOAL_STATUS_COLORS[g.status]}`}>{g.status.replace(/_/g,' ')}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">{g.employee?.firstName} {g.employee?.lastName} · {g.weightage}% weight</p>
                  {g.description && <p className="text-sm text-gray-600">{g.description}</p>}
                  {g.employeeNote && <p className="text-xs text-gray-400 italic mt-0.5">Employee: {g.employeeNote}</p>}
                  {g.selfRating && <p className="text-xs text-purple-700 mt-1">Self Rating: {g.selfRating}/5 {g.selfComment && `— "${g.selfComment}"`}</p>}
                </div>
                <div className="flex flex-col gap-1 items-end">
                  {g.status === 'pending_approval' && (
                    <>
                      <button onClick={() => handleReviewGoal(g._id, 'approve', '')} className="text-xs text-green-600 hover:underline">Approve</button>
                      <button onClick={() => handleReviewGoal(g._id, 'request_resubmit', '')} className="text-xs text-red-500 hover:underline">Request Resubmit</button>
                    </>
                  )}
                  {g.status === 'approved' && g.selfRating && !g.managerRating && (
                    <button onClick={() => { setShowRating(g); setRatingForm({ managerRating:'', managerComment:'' }); }} className="text-xs text-blue-600 hover:underline">Rate Employee</button>
                  )}
                  {g.managerRating && <span className="text-xs text-green-700">Rated: {g.managerRating}/5 ⭐</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Outcomes */}
      {tab === 'outcomes' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>{['Employee','KPI Score','Outcome','Notes'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {outcomes.length === 0
                ? <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-400">No outcomes set yet</td></tr>
                : outcomes.map(o => {
                    const oc = OUTCOMES.find(x => x.v === o.outcome);
                    return (
                      <tr key={o._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{o.employee?.firstName} {o.employee?.lastName}</td>
                        <td className="px-4 py-3 font-bold text-blue-700">{o.kpiScore ? `${parseFloat(o.kpiScore).toFixed(2)}/5` : '—'}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${oc?.color}`}>{oc?.l}</span></td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{o.notes || '—'}</td>
                      </tr>
                    );
                  })
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Manager Rating Modal */}
      {showRating && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Manager Rating — {showRating.title}</h2>
              <button onClick={() => setShowRating(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <div><p className="text-sm text-gray-500 mb-1">Employee's self rating: <strong>{showRating.selfRating}/5</strong></p>{showRating.selfComment && <p className="text-xs italic text-gray-400">"{showRating.selfComment}"</p>}</div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating (1–5) *</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(r=>(
                    <button key={r} onClick={() => setRatingForm(f=>({...f,managerRating:r}))} className={`w-10 h-10 rounded-full font-bold text-sm border-2 transition-all ${ratingForm.managerRating===r?'border-blue-500 bg-blue-500 text-white':'border-gray-200 text-gray-700 hover:border-blue-300'}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                <textarea value={ratingForm.managerComment} onChange={e=>setRatingForm(f=>({...f,managerComment:e.target.value}))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
              <div className="flex gap-3">
                <button onClick={() => setShowRating(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleManagerRating} disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving?'Submitting...':'Submit Rating'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set Outcome Modal */}
      {showOutcome && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="font-semibold text-gray-900">Set Outcome — {showOutcome.employee?.firstName} {showOutcome.employee?.lastName}</h2>
                {showOutcome.kpiScore && (
                  <p className="text-xs text-blue-600 mt-0.5">KPI Score: <strong>{showOutcome.kpiScore}/5</strong> · Auto-suggested outcome highlighted</p>
                )}
              </div>
              <button onClick={() => setShowOutcome(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Outcome *</label>
                <div className="grid grid-cols-1 gap-2">
                  {OUTCOMES.map(o => {
                    const isSuggested = suggestOutcome(showOutcome.kpiScore) === o.v;
                    return (
                      <label key={o.v} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${outcomeForm.outcome===o.v?'border-blue-500 bg-blue-50':'border-gray-200 hover:border-gray-300'}`}>
                        <input type="radio" name="outcome" checked={outcomeForm.outcome===o.v} onChange={() => setOutcomeForm(f=>({...f,outcome:o.v}))} className="w-4 h-4 text-blue-600"/>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${o.color}`}>{o.l}</span>
                        {isSuggested && <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Suggested</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={outcomeForm.notes} onChange={e=>setOutcomeForm(f=>({...f,notes:e.target.value}))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Reason or additional notes..."/></div>
              <div className="flex gap-3">
                <button onClick={() => setShowOutcome(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleSetOutcome} disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving?'Saving...':'Save Outcome'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PmsPage;
